import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { authV2Selector, type AuthMagicOTP } from "@/store/slices/authV2Slice";

const MAGIC_OTP_COOLDOWN_MS = 45_000;

const normalizeMagicOtpEmail = (email: string | null | undefined) => {
  return email?.trim().toLowerCase() ?? "";
};

function getMagicOtpCooldownRemainingMs(
  magicOTP: AuthMagicOTP | null | undefined,
  email: string | null | undefined,
  now = Date.now(),
) {
  const triggeredAt = magicOTP?.triggeredAt;
  const storedEmail = normalizeMagicOtpEmail(magicOTP?.email);
  const currentEmail = normalizeMagicOtpEmail(email);

  if (typeof triggeredAt !== "number" || !storedEmail || !currentEmail || storedEmail !== currentEmail) {
    return 0;
  }

  return Math.min(MAGIC_OTP_COOLDOWN_MS, Math.max(MAGIC_OTP_COOLDOWN_MS - (now - triggeredAt), 0));
}

const useMagicOtpCoolDown = (email: string | null | undefined) => {
  const authV2 = useSelector(authV2Selector);
  const [now, setNow] = useState(() => Date.now());
  const magicOtpCooldownRemainingMs = getMagicOtpCooldownRemainingMs(authV2?.magicOTP, email, now);
  const magicOtpCooldownRemainingSeconds = Math.ceil(magicOtpCooldownRemainingMs / 1000);
  const magicOtpCooldownActive = magicOtpCooldownRemainingMs > 0;

  useEffect(() => {
    setNow(Date.now());
  }, [authV2?.magicOTP?.triggeredAt, email]);

  useEffect(() => {
    if (!magicOtpCooldownRemainingMs) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setNow(Date.now());
    }, Math.min(magicOtpCooldownRemainingMs, 1000));

    return () => window.clearTimeout(timer);
  }, [magicOtpCooldownRemainingMs]);

  return {
    magicOtpCooldownActive,
    magicOtpCooldownRemainingMs,
    magicOtpCooldownRemainingSeconds,
  };
};

export { MAGIC_OTP_COOLDOWN_MS, getMagicOtpCooldownRemainingMs, useMagicOtpCoolDown };
